import type {
  AISessionInteractiveOptions,
  AISessionMessage,
  AISessionRun,
  AISessionSseEvent,
} from '@/types/ai-session';
import type { CanonicalAISession } from '@/lib/ai-session/contracts/types';
import { getBusinessErrorCode } from '@/lib/error-utils';
import { AI_SESSION_BILLING_ERROR_MESSAGES } from '../presentation/insufficient-credit-error';

import { ALIGNMENT_EDIT_CLARIFICATION_TYPE } from '../capabilities/alignment/alignment-edit-question';
import type { AISessionAssetItem, AISessionAssetResult } from '../presentation/composer/mentions';

export type {
  AISessionAssetItem,
  AISessionAssetResult,
  FollowUpMentionSuggestion,
  LeadingMention,
} from '../presentation/composer/mentions';
export {
  applyFollowUpMention,
  buildAssetMentionSuggestions,
  buildStoryboardMentionSuggestions,
  getFollowUpMentionQuery,
  getLeadingMention,
} from '../presentation/composer/mentions';

/** 与后端 RUN_CONFLICT_USER_MESSAGES 对齐：内部机器码不直接展示给用户 */
export const RUN_CONFLICT_USER_MESSAGES: Record<string, string> = {
  RUN_BASE_VERSION_CONFLICT: '内容已更新，请刷新后基于最新结果继续操作',
  RUN_BUSINESS_STATE_CONFLICT: '当前阶段状态已变化，请刷新后查看最新进度',
  RUN_STATE_CONFLICT: '执行状态已变化，请刷新后查看最新进度',
  RUN_CHECKPOINT_CONFLICT: '检查点已变化，请刷新后查看最新进度',
  RUN_PLAN_HASH_CONFLICT: '待确认计划已变化，请重新查看并确认',
  RUN_CONFIRMATION_RUN_CONFLICT: '当前确认任务已失效或被更新，请继续确认以生成修订方案',
  RUN_CONFIRMATION_ACTOR_CONFLICT: '确认操作者已变化，请重新发起操作',
  RUN_CONFIRMATION_ORGANIZATION_CONFLICT: '当前组织已变化，请重新发起操作',
  RUN_CONFIRMATION_ARTIFACT_VERSION_CONFLICT: '待确认内容版本已变化，请重新生成计划',
  ALIGNMENT_CLARIFICATION_VERSION_CONFLICT: '通读建议已更新，请基于最新建议重新调整',
};

/** 技能准入拒绝按机器码呈现，旧 Run 的内部文案不直接暴露。 */
export const AI_SESSION_PERMISSION_ERROR_MESSAGES: Record<string, string> = {
  SKILL_RUNTIME_USE_FORBIDDEN: '当前账号无法使用所选分析技能，请联系管理员确认授权。',
  SKILL_RUNTIME_WORKFLOW_FORBIDDEN: '当前账号无法使用本轮分析技能，请联系管理员确认工作流权限。',
};

/** 与后端 AI_SESSION_MACHINE_CODE_USER_MESSAGES 对齐（能力冲突等非 409 冲突码） */
export const AI_SESSION_MACHINE_CODE_USER_MESSAGES: Record<string, string> = {
  ...AI_SESSION_BILLING_ERROR_MESSAGES,
  ...AI_SESSION_PERMISSION_ERROR_MESSAGES,
  CHAT_CONTEXT_BUDGET_EXCEEDED:
    '本轮内容超过当前模型的上下文容量，内容尚未修改。请缩小引用范围或开启新会话。',
  STORYBOARD_MERGE_NONCONTIGUOUS:
    '只能按顺序合并连续镜头。请改选同一场次内相邻的镜头；当前分镜未修改。',
  STORYBOARD_MERGE_CROSS_SCENE: '只能合并同一场次的镜头。请分别处理各场次；当前分镜未修改。',
  PROPOSAL_NO_VISIBLE_CHANGE: '没有发现和当前草稿不同的修改，内容保持原样。',
  BREAKDOWN_REFERENCE_SCOPE_ESCALATION:
    '本次修改包含未选中的内容，暂时无法应用。请仅修改已选内容；如需修改其他内容，请先重新选择。',
  BREAKDOWN_REFERENCE_DIFF_SCOPE_ESCALATION:
    '本次修改包含未选中的内容，暂时无法应用。请仅修改已选内容；如需修改其他内容，请先重新选择。',
  PROPOSAL_PREVIEW_GENERATION_FAILED:
    '暂时没能生成可应用的修改预览，当前内容尚未修改。原请求和已补充的信息已保留，可点击“重试”继续，无需重新描述需求。',
  RUN_CAPABILITY_CONFLICT: '当前操作与本阶段能力冲突，请拆成单次修改，或先完成当前阶段后再继续',
  ATTEMPT_RESERVATION_IDEMPOTENCY_CONFLICT: '计费调用冲突，请稍后重试',
  PROVIDER_HUB_CHAT_RUNTIME_BIND_FAILED: '模型运行时绑定失败，请稍后重试',
  PROVIDER_HUB_ACTIVE_MODEL_REQUIRED: '模型未就绪，请稍后重试',
  RUN_CONFIRMATION_PAYLOAD_INVALID: '确认内容无效，请刷新后重试',
  RUN_SERVICE_RISK_BINDING_MISSING: '服务风控绑定缺失，请稍后重试',
  TOOL_FIRST_PROPOSAL_UNGROUNDED: '提案无法落地到当前资产草稿，请补充具体要改的资产后重试',
  RUN_CONFIRMATION_ACTOR_REQUIRED: '缺少确认操作者，请重新登录后重试',
  CLIENT_VERSION_UNSUPPORTED: '当前客户端版本过旧，请刷新页面后重试',
  EMPTY_TERMINAL_ANSWER: '本轮未给出有效答复，请换个描述再试。',
  UNKNOWN_TERMINAL_ACTION: '本轮无法完成资产交互。',
  UNKNOWN_POLICY_OUTCOME: '本轮无法完成资产交互。',
  UNSUPPORTED_ACTION_POLICY_PAIR: '本轮动作无法执行，请换个描述再试。',
  TERMINAL_RESULT_INVALID: '本轮结果无效，请换个描述再试。',
  TERMINAL_TOOL_REQUIRED: '本次未生成内容，请重试。',
  CLARIFICATION_COUNT_INVALID: '本轮无法完成资产交互。',
  PROVIDER_OUTPUT_LENGTH_EXCEEDED: '输出过长被截断，请重试',
  SCREENPLAY_VERSION_NOT_READY: '剧本分析数据更新中，请稍后再开始 AI 解析',
  AI_SESSION_RUN_MODEL_BUDGET_EXHAUSTED: '本次生成尝试次数过多，已自动停止，请稍后重试',
};

/** T1-19：failureCategory → 用户文案。不得使用「请补充你的修改意图」。 */
export const FAILURE_CATEGORY_USER_MESSAGES: Record<string, string> = {
  provider_timeout: 'AI 服务暂时超时，请稍后重试。',
  provider_rate_limit: 'AI 服务繁忙，请稍后重试。',
  provider_transport: 'AI 服务暂不可用，请稍后重试。',
  model_schema_invalid: '本轮结果无法识别，请稍后重试。',
  runtime_internal: '本轮无法完成资产交互。',
};

/** REC-03：内部提案恢复不得转成「换一种描述 / 补版本」。 */
export const RECOVERY_KIND_USER_MESSAGES: Record<string, string> = {
  internal_proposal: AI_SESSION_MACHINE_CODE_USER_MESSAGES.PROPOSAL_PREVIEW_GENERATION_FAILED,
  version_conflict: '草稿已更新，当前修改未写入。请基于最新草稿再试。',
};

export const FAILURE_CATEGORY_ERROR_CLASS: Record<string, string> = {
  provider_timeout: 'retryable_provider',
  provider_rate_limit: 'retryable_provider',
  provider_transport: 'retryable_provider',
  model_schema_invalid: 'invalid_model_output',
  runtime_internal: 'runtime_failure',
};

export function frontendErrorClassForFailureCategory(category: string): string | undefined {
  return FAILURE_CATEGORY_ERROR_CLASS[category];
}

export function isFailureCategoryUserClarification(_category: string): boolean {
  return false;
}

const MEANINGLESS_REPHRASE_RE = /请换[一种个]|请补充.*版本|缺少可执行的资产对象或版本/;

function readNestedStringField(error: unknown, key: string): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const record = error as Record<string, unknown>;
  const candidates: unknown[] = [record[key]];
  const payload = record.outputPayload;
  if (payload && typeof payload === 'object') {
    candidates.push((payload as Record<string, unknown>)[key]);
  }
  const data = record.data;
  if (data && typeof data === 'object') {
    const nested = data as Record<string, unknown>;
    candidates.push(nested[key]);
    const inner = nested.data;
    if (inner && typeof inner === 'object') {
      candidates.push((inner as Record<string, unknown>)[key]);
    }
  }
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }
  return undefined;
}

function readRecoveryKind(error: unknown): string | undefined {
  const kind = readNestedStringField(error, 'recoveryKind');
  if (kind && RECOVERY_KIND_USER_MESSAGES[kind]) return kind;
  const status = readNestedStringField(error, 'recoveryStatus');
  if (status === 'internal_validation_failed' || status === 'exhausted') {
    return 'internal_proposal';
  }
  return undefined;
}

function resolveRecoveryUserMessage(error: unknown): string | undefined {
  const kind = readRecoveryKind(error);
  if (!kind) return undefined;
  const raw = readExplicitMessage(error);
  if (raw && !MACHINE_CODE_LIKE_RE.test(raw) && !MEANINGLESS_REPHRASE_RE.test(raw)) {
    return raw;
  }
  return RECOVERY_KIND_USER_MESSAGES[kind];
}

function readFailureCategory(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const record = error as Record<string, unknown>;
  const candidates: unknown[] = [record.failureCategory];
  const payload = record.outputPayload;
  if (payload && typeof payload === 'object') {
    candidates.push((payload as Record<string, unknown>).failureCategory);
  }
  const data = record.data;
  if (data && typeof data === 'object') {
    const nested = data as Record<string, unknown>;
    candidates.push(nested.failureCategory);
    const inner = nested.data;
    if (inner && typeof inner === 'object') {
      candidates.push((inner as Record<string, unknown>).failureCategory);
    }
  }
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && FAILURE_CATEGORY_USER_MESSAGES[candidate]) {
      return candidate;
    }
  }
  return undefined;
}

const STORYBOARD_FAILURE_USER_MESSAGE = '分镜生成暂时失败，请在助手中重新生成并查看进度';

const STORYBOARD_TECH_DIAGNOSTIC_RE =
  /PROVIDER_HUB_[A-Z0-9_]+|拆小后仍失败|左段\s*[:：]|右段\s*[:：]|STAGE_STREAM_FAILED/i;

/** 历史 run.errorMessage 中的旧冲突话术 → 新用户文案 */
const LEGACY_RUN_CONFLICT_USER_MESSAGES: Record<string, string> = {
  '待确认任务已变化，请刷新后重试': RUN_CONFLICT_USER_MESSAGES.RUN_CONFIRMATION_RUN_CONFLICT,
  '当前阶段状态已变化，请刷新后重试': RUN_CONFLICT_USER_MESSAGES.RUN_BUSINESS_STATE_CONFLICT,
  '执行状态已变化，请刷新后重试': RUN_CONFLICT_USER_MESSAGES.RUN_STATE_CONFLICT,
  '检查点已变化，请刷新后重试': RUN_CONFLICT_USER_MESSAGES.RUN_CHECKPOINT_CONFLICT,
};

function resolveRunConflictUserMessage(error: unknown): string | undefined {
  const code = getBusinessErrorCode(error);
  if (code && RUN_CONFLICT_USER_MESSAGES[code]) {
    return RUN_CONFLICT_USER_MESSAGES[code];
  }
  return undefined;
}

function resolveMachineCodeUserMessage(error: unknown): string | undefined {
  const code = getBusinessErrorCode(error);
  if (code && AI_SESSION_MACHINE_CODE_USER_MESSAGES[code]) {
    return AI_SESSION_MACHINE_CODE_USER_MESSAGES[code];
  }
  return undefined;
}

/** 服务端 Provider 失败稳定码（PROVIDER_<ProviderErrorType>），文案由后端 ai_errors 规则表下发 */
const PROVIDER_FAILURE_CODE_RE = /^PROVIDER_[A-Z][A-Z0-9_]{1,62}$/;
/** 形似机器码的文本（全大写下划线）不是用户文案 */
const MACHINE_CODE_LIKE_RE = /^[A-Z][A-Z0-9_]{2,63}$/;

function readExplicitMessage(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const message = (error as { message?: unknown }).message;
  if (typeof message !== 'string') return undefined;
  const trimmed = message.trim();
  return trimmed || undefined;
}

/**
 * PROVIDER_ 稳定码的文案以服务端规则表为准（可运营配置），不能被 failureCategory 的通用文案覆盖；
 * 没有下发文案（历史 Run 只有机器码）时返回 undefined，交给 failureCategory / 回退处理。
 */
function resolveProviderRuleMessage(error: unknown): string | undefined {
  const code = getBusinessErrorCode(error);
  if (!code || !PROVIDER_FAILURE_CODE_RE.test(code)) return undefined;
  const message = readExplicitMessage(error);
  if (!message || MACHINE_CODE_LIKE_RE.test(message)) return undefined;
  return message;
}

export const SESSION_TYPE_SCREENPLAY_ALIGNMENT = 1;
export const SESSION_TYPE_SCREENPLAY_ASSET_BREAKDOWN = 3;
export const SESSION_TYPE_SCREENPLAY_STORYBOARD_BREAKDOWN = 4;
export const SUPPORTED_AI_SESSION_TYPES = new Set<number>([
  SESSION_TYPE_SCREENPLAY_ALIGNMENT,
  SESSION_TYPE_SCREENPLAY_ASSET_BREAKDOWN,
  SESSION_TYPE_SCREENPLAY_STORYBOARD_BREAKDOWN,
]);
export const SESSION_STATUS_COMPLETED = 3;

export function isSupportedAISessionType(sessionType: unknown): boolean {
  return typeof sessionType === 'number' && SUPPORTED_AI_SESSION_TYPES.has(sessionType);
}

/** Tail 确认卡状态（由 Run 驱动，不再从 messages 启发式恢复）。 */
export type AlignmentRemoveConfirmationState = {
  message: string;
  sourceComplaint?: string;
  targetSections?: string[];
  targetSectionTitles?: string[];
  /** remove=删除；restore=精确恢复（默认 remove） */
  variant?: 'remove' | 'restore';
};

export type AlignmentEditConfirmationState = {
  message: string;
  sourceComplaint?: string;
  targetSections?: string[];
  plan?: Record<string, unknown>;
  /** 明确、低风险的单节修订会自动续跑，仅展示生成预览进度。 */
};

export function getLatestQuestion(
  messages: AISessionMessage[]
): AISessionInteractiveOptions | null {
  // 只认「当前仍待答」的问题：遇用户回复 / stage clarification / 普通 AI 文本则停止，
  // 避免跳过无 questionId 的阶段澄清后复活更早的 edit clarification（幽灵澄清态）。
  for (const message of [...messages].reverse()) {
    if (message.senderType === 'user') {
      return null;
    }
    if (message.senderType !== 'ai') {
      continue;
    }

    const options = message.interactiveOptions;
    const optionType = options?.type;
    if (optionType === 'stage_clarification') {
      return null;
    }

    const questionId = options?.questionId;
    const choiceCount = options?.options?.length ?? 0;
    const isEditClarification = optionType === ALIGNMENT_EDIT_CLARIFICATION_TYPE;
    if (questionId && (choiceCount > 0 || isEditClarification)) {
      // 无可用选项的 edit clarification 不锁 composer / 不覆盖 placeholder。
      if (isEditClarification && choiceCount === 0) {
        return null;
      }
      return options;
    }

    if ((message.content || '').trim()) {
      return null;
    }
  }
  return null;
}

export function getStringData(event: AISessionSseEvent, key: string): string {
  const value = event.data[key];
  return typeof value === 'string' ? value : '';
}

/** StageEvent SSE 载荷：业务字段在 `data.data`，会话 ID 等在顶层。 */
export function readStageEventField(event: AISessionSseEvent, key: string): unknown {
  const nested = event.data.data;
  if (nested && typeof nested === 'object' && !Array.isArray(nested) && key in nested) {
    return (nested as Record<string, unknown>)[key];
  }
  return event.data[key];
}

export function getStageEventStringField(
  event: AISessionSseEvent,
  key: string
): string | undefined {
  const value = readStageEventField(event, key);
  return typeof value === 'string' ? value : undefined;
}

export function getStageEventBooleanField(
  event: AISessionSseEvent,
  key: string
): boolean | undefined {
  const value = readStageEventField(event, key);
  return typeof value === 'boolean' ? value : undefined;
}

export function getNumberData(event: AISessionSseEvent, key: string): number | undefined {
  const value = event.data[key];
  return typeof value === 'number' ? value : undefined;
}

export function getStageEventMessage(event: AISessionSseEvent): string {
  const nested = event.data.data;
  if (nested && typeof nested === 'object' && 'message' in nested) {
    const message = (nested as { message?: unknown }).message;
    return typeof message === 'string' ? message : '';
  }
  return getStringData(event, 'message');
}

export function getStageEventStep(event: AISessionSseEvent): string | null {
  const nested = event.data.data;
  if (nested && typeof nested === 'object' && 'step' in nested) {
    const step = (nested as { step?: unknown }).step;
    return typeof step === 'string' ? step : null;
  }
  const directStep = getStringData(event, 'step');
  return directStep || null;
}

function readStageEventNumber(event: AISessionSseEvent, key: string): number | null {
  const nested = event.data.data;
  if (nested && typeof nested === 'object' && key in nested) {
    const value = (nested as Record<string, unknown>)[key];
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }
  const direct = getNumberData(event, key);
  return direct ?? null;
}

export function getStageEventBatchProgress(event: AISessionSseEvent): {
  batchIndex: number | null;
  batchTotal: number | null;
} {
  return {
    batchIndex:
      readStageEventNumber(event, 'progressBatchIndex') ??
      readStageEventNumber(event, 'batchIndex'),
    batchTotal:
      readStageEventNumber(event, 'progressBatchTotal') ??
      readStageEventNumber(event, 'batchTotal'),
  };
}

/** 契约校验内部路径不向用户展示，仅保留可操作指引 */
export const AI_SESSION_CONTRACT_VALIDATION_USER_MESSAGE =
  '会话数据异常，请刷新后重试；若仍失败，请重新开始分析。';

function isAISessionContractValidationMessage(message: string): boolean {
  return (
    /^AI Session\b/.test(message) ||
    /\bstoryboardResult\.storyboards\[\d+\]\./.test(message) ||
    /\bpayload\.(?:assetResult|alignment|storyboardResult)\b/.test(message)
  );
}

const TRANSPORT_UNAVAILABLE_USER_MESSAGE = '服务暂时不可用，请稍后重试。';

/**
 * HTTP 客户端的英文传输报错不向用户展示。
 * 返回 undefined 表示不是传输报错；返回 null 表示改用调用方的兜底文案。
 */
function resolveTransportErrorUserMessage(message: string): string | null | undefined {
  const status = /^Request failed with status code (\d{3})$/.exec(message)?.[1];
  if (status) return status.startsWith('5') ? TRANSPORT_UNAVAILABLE_USER_MESSAGE : null;
  if (message === 'Network Error' || /^timeout of \d+ms exceeded$/.test(message)) {
    return '网络连接异常，请检查网络后重试。';
  }
  return undefined;
}

/** React 渲染死循环不是模型失败，不能当成 Run 错误文案展示。 */
export function isReactUpdateDepthError(error: unknown): boolean {
  return error instanceof Error && error.message.includes('Maximum update depth exceeded');
}

/** 浏览器 AbortSignal 与 Axios 请求取消属于正常退出，不进入业务失败/扣费恢复。 */
export function isAISessionCancellationError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const cancellation = error as { name?: unknown; code?: unknown };
  return cancellation.name === 'AbortError' || cancellation.code === 'ERR_CANCELED';
}

/** 普通断流需要业务对账；取消或权限拒绝后不再额外读取。 */
export function shouldRefreshAISessionAfterError(error: unknown): boolean {
  if (isAISessionCancellationError(error)) return false;
  const failure = error as { code?: unknown; response?: { status?: number } } | null;
  const status = failure?.response?.status ?? failure?.code;
  return status !== 401 && status !== 403;
}

export function normalizeAISessionErrorMessage(error: unknown, fallback: string): string {
  const mappedFromCode = resolveRunConflictUserMessage(error);
  if (mappedFromCode) return mappedFromCode;

  const providerRuleMessage = resolveProviderRuleMessage(error);
  if (providerRuleMessage) return providerRuleMessage;

  const recoveryMessage = resolveRecoveryUserMessage(error);
  if (recoveryMessage) return recoveryMessage;

  const mappedMachineFromCode = resolveMachineCodeUserMessage(error);
  if (mappedMachineFromCode) return mappedMachineFromCode;

  const mappedCategory = readFailureCategory(error);
  if (mappedCategory) {
    return FAILURE_CATEGORY_USER_MESSAGES[mappedCategory];
  }

  const objectMessage =
    error &&
    typeof error === 'object' &&
    typeof (error as { message?: unknown }).message === 'string'
      ? (error as { message: string }).message
      : null;
  const rawMessage =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : (objectMessage ?? fallback);
  const message = rawMessage
    .trim()
    .replace(/^(?:HTTP\s*)?(?:Error\s*)?\d{3}\s*[:：]\s*/i, '')
    .trim();
  if (!message) return fallback;
  if (message === 'STAGE_STREAM_FAILED') return 'AI 处理失败，请稍后重试。';
  const transportMessage = resolveTransportErrorUserMessage(message);
  if (transportMessage !== undefined) return transportMessage ?? fallback;

  const mappedFromMessage = RUN_CONFLICT_USER_MESSAGES[message];
  if (mappedFromMessage) return mappedFromMessage;

  const mappedMachineFromMessage = AI_SESSION_MACHINE_CODE_USER_MESSAGES[message];
  if (mappedMachineFromMessage) return mappedMachineFromMessage;

  const mappedFailureCategory = FAILURE_CATEGORY_USER_MESSAGES[message];
  if (mappedFailureCategory) return mappedFailureCategory;

  const mappedFromLegacy = LEGACY_RUN_CONFLICT_USER_MESSAGES[message];
  if (mappedFromLegacy) return mappedFromLegacy;

  if (isAISessionContractValidationMessage(message)) {
    return AI_SESSION_CONTRACT_VALIDATION_USER_MESSAGE;
  }

  if (
    message.includes('response_format') &&
    message.includes('json_object') &&
    message.includes('not supported by this model')
  ) {
    return '当前模型不支持结构化 JSON 输出，请重新生成；如果仍失败，请切换支持 JSON 输出的模型。';
  }

  if (
    /\bvalidation errors? for\b/i.test(message) ||
    /errors\.pydantic\.dev/i.test(message) ||
    /\[type=[^,\]]+,\s*input_value=/i.test(message) ||
    /(?:^|\n)\s*Traceback \(most recent call last\):/i.test(message)
  ) {
    return fallback;
  }

  // 长文案中嵌套机器码时，整句降级为可读文案，避免半脱敏。
  for (const code of Object.keys(AI_SESSION_MACHINE_CODE_USER_MESSAGES)) {
    if (message.includes(code)) {
      return AI_SESSION_MACHINE_CODE_USER_MESSAGES[code] ?? fallback;
    }
  }

  const providerMessage = message.match(/['"]message['"]:\s*['"]([^'"]+)['"]/);
  if (providerMessage?.[1]) {
    return providerMessage[1];
  }
  // PROVIDER_<TYPE> 稳定码没有下发文案且无分类：不裸露机器码
  if (PROVIDER_FAILURE_CODE_RE.test(message)) return fallback;
  return message;
}

/** 分镜失败对外文案：去掉批次/机器码诊断，只保留可行动提示。 */
export function sanitizeStoryboardFailureMessage(
  error: unknown,
  fallback: string = STORYBOARD_FAILURE_USER_MESSAGE
): string {
  const rawMessage =
    error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  if (
    STORYBOARD_TECH_DIAGNOSTIC_RE.test(rawMessage) ||
    /所有分镜批次均失败/.test(rawMessage) ||
    /分镜批次\s*\d+/.test(rawMessage)
  ) {
    return fallback;
  }
  return normalizeAISessionErrorMessage(error, fallback) || fallback;
}

/** 批次失败仅展示计数，不回显原始 message。 */
export function formatStoryboardBatchFailureHint(
  batchErrors: ReadonlyArray<{ batchIndex?: number; message?: string }> | undefined
): string | null {
  const count = batchErrors?.length ?? 0;
  if (count <= 0) return null;
  return `共 ${count} 批未完成`;
}

export function isAISessionNotFoundError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const record = error as {
    code?: unknown;
    response?: {
      status?: unknown;
    };
  };
  return record.code === 404 || record.response?.status === 404;
}

export function getStageEventFailureMessage(event: AISessionSseEvent, fallback: string): string {
  return normalizeAISessionErrorMessage(
    {
      message: getStageEventMessage(event) || getStringData(event, 'message'),
      failureCategory: getStageEventStringField(event, 'failureCategory'),
      errorCode: getStageEventStringField(event, 'code'),
    },
    fallback
  );
}

export function resolveAISessionRunFailureMessage(
  run: Pick<AISessionRun, 'errorMessage' | 'outputPayload' | 'failureCategory'> | null | undefined,
  message: string | null | undefined,
  fallback: string
): string {
  const payload = run?.outputPayload ?? {};
  const categoryFromPayload =
    typeof payload.failureCategory === 'string' ? payload.failureCategory : undefined;
  const recoveryKindFromPayload =
    typeof payload.recoveryKind === 'string' ? payload.recoveryKind : undefined;
  const recoveryStatusFromPayload =
    typeof payload.recoveryStatus === 'string' ? payload.recoveryStatus : undefined;
  // 失败 Run 的 errorMessage 是机器码（errorCode 契约），用户文案在终态 outputPayload.message
  const messageFromPayload =
    typeof payload.message === 'string' && payload.message.trim() ? payload.message : undefined;
  return normalizeAISessionErrorMessage(
    {
      message: message ?? messageFromPayload ?? run?.errorMessage,
      failureCategory: run?.failureCategory ?? categoryFromPayload,
      recoveryKind: recoveryKindFromPayload,
      recoveryStatus: recoveryStatusFromPayload,
      errorCode: typeof run?.errorMessage === 'string' ? run.errorMessage : undefined,
    },
    fallback
  );
}

export function getSessionSkillLabel(session: CanonicalAISession | null): string {
  if (session?.sessionType === SESSION_TYPE_SCREENPLAY_ASSET_BREAKDOWN) {
    return '资产拆解会话';
  }
  if (session?.sessionType === SESSION_TYPE_SCREENPLAY_STORYBOARD_BREAKDOWN) {
    return '分镜拆解会话';
  }
  const snapshot = getSessionSkillSnapshot(session);
  if (!snapshot || typeof snapshot !== 'object') return '系统通读 Skill';
  const skill = snapshot as { label?: unknown; name?: unknown };
  if (typeof skill.label === 'string' && skill.label.trim()) return skill.label;
  if (typeof skill.name === 'string' && skill.name.trim()) return skill.name;
  return '系统通读 Skill';
}

export function getSessionStageKey(session: CanonicalAISession | null): string {
  if (session?.sessionType === SESSION_TYPE_SCREENPLAY_ASSET_BREAKDOWN) {
    return 'asset_breakdown';
  }
  if (session?.sessionType === SESSION_TYPE_SCREENPLAY_STORYBOARD_BREAKDOWN) {
    return 'storyboard_breakdown';
  }
  return 'alignment';
}

export function getLockedStageModelName(
  session: CanonicalAISession | null,
  stageKey = getSessionStageKey(session)
): string | null {
  const payload = session?.payload;
  if (!payload) return null;

  const stageModels = payload.stageModelNames;
  if (stageModels && typeof stageModels === 'object' && !Array.isArray(stageModels)) {
    const value = (stageModels as Record<string, unknown>)[stageKey];
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }

  const modelName = payload.modelName;
  return typeof modelName === 'string' && modelName.trim() ? modelName : null;
}

export function isStageModelRelockAllowed(stageKey: string): boolean {
  // 分镜拆解阶段整体重拆是新一轮草稿生成，允许换锁模型；
  // 其他阶段保持"阶段内锁定"语义。follow-up 局部修改仍使用已锁定模型。
  return stageKey === 'storyboard_breakdown';
}

export function getSessionSkillId(session: CanonicalAISession | null): string | null {
  const snapshot = getSessionSkillSnapshot(session);
  if (!snapshot || typeof snapshot !== 'object') return null;
  const skill = snapshot as { skillId?: unknown };
  return typeof skill.skillId === 'string' && skill.skillId.trim() ? skill.skillId : null;
}

export function getSessionSkillVersionId(session: CanonicalAISession | null): string | null {
  const snapshot = getSessionSkillSnapshot(session);
  if (!snapshot || typeof snapshot !== 'object') return null;
  const skill = snapshot as { bundleVersionId?: unknown };
  return typeof skill.bundleVersionId === 'string' && skill.bundleVersionId.trim()
    ? skill.bundleVersionId.trim()
    : null;
}

export function getSessionSkillConfigHash(session: CanonicalAISession | null): string | null {
  const snapshot = getSessionSkillSnapshot(session);
  if (!snapshot || typeof snapshot !== 'object') return null;
  const skill = snapshot as { skillConfigHash?: unknown };
  return typeof skill.skillConfigHash === 'string' && skill.skillConfigHash.trim()
    ? skill.skillConfigHash.trim()
    : null;
}

export function hasSessionAlignmentInteraction(session: CanonicalAISession | null): boolean {
  if (session?.sessionType !== SESSION_TYPE_SCREENPLAY_ALIGNMENT) return false;
  const payload = session.payload;
  const alignment = getRecord(payload.alignment);
  const answers = alignment?.answers;
  const compiledSuggestions = alignment?.compiledSuggestions;
  return Boolean(
    (Array.isArray(answers) && answers.length > 0) ||
    (typeof compiledSuggestions === 'string' && compiledSuggestions.trim())
  );
}

export function getScriptTypeChineseLabel(type: string | null | undefined): string {
  if (!type) return '';
  const mapping: Record<string, string> = {
    ad: '广告',
    movie: '电影',
    series: '剧集',
    anime: '动漫',
    short: '短剧',
    concept: '概念',
    general: '通用',
  };
  const normalized = type.toLowerCase().trim();
  return mapping[normalized] || type;
}

function getSessionSkillSnapshot(session: CanonicalAISession | null): unknown {
  if (!session) return null;
  if (session.sessionType === SESSION_TYPE_SCREENPLAY_ALIGNMENT) {
    return session.payload.alignment.skillSnapshot ?? null;
  }
  const extra = getRecord(session.payload);
  return extra?.skillSnapshot ?? null;
}

function getRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

export function getSessionAssetResult(
  session: CanonicalAISession | null
): AISessionAssetResult | null {
  if (session?.sessionType !== SESSION_TYPE_SCREENPLAY_ASSET_BREAKDOWN) return null;
  const assetResult = session.payload?.assetResult ?? session.payload?.assetChecklistDraft;
  if (!assetResult || typeof assetResult !== 'object') return null;
  const source = assetResult as Record<string, unknown>;
  const result: AISessionAssetResult = {
    characters: normalizeAssetItems(source.characters),
    locations: normalizeAssetItems(source.locations),
    props: normalizeAssetItems(source.props),
  };
  const total = result.characters.length + result.locations.length + result.props.length;
  return total > 0 ? result : null;
}

function normalizeAssetItems(value: unknown): AISessionAssetItem[] {
  if (!Array.isArray(value)) return [];
  return value.reduce<AISessionAssetItem[]>((items, item) => {
    if (!item || typeof item !== 'object') return items;
    const record = item as Record<string, unknown>;
    const name = typeof record.name === 'string' ? record.name.trim() : '';
    if (!name) return items;
    const description =
      typeof record.description === 'string' && record.description.trim()
        ? record.description.trim()
        : undefined;
    items.push({
      name,
      description,
      ...(typeof record.draftItemId === 'string' ? { draftItemId: record.draftItemId } : {}),
    });
    return items;
  }, []);
}
